'use client';

import { useEffect } from 'react';

/**
 * Swallow a file drop that no drop target accepted (VALE-1592).
 *
 * **The browser's default for a dropped file is to navigate to it.** Not to ignore it — to
 * leave the application and render the file in the tab. So a miss by twenty pixels beside
 * an upload dialog does not merely fail to upload: it destroys whatever the reader had
 * typed, and they come back through the matter, the Documents tab and the dialog to try
 * again. Enlarging a drop target lowers the odds of a miss; only this makes a miss cheap.
 *
 * Mounted once, in the root layout (`app/layout.tsx`), which every route group nests under —
 * so there is one mount and no page is left unguarded. Route handlers under `app/api` render
 * no UI, and `app/global-error.tsx` replaces the root layout only when the layout itself has
 * already failed; neither is a page a reader can drop a file onto.
 *
 * ## Why this cannot break a real drop target
 *
 * The listeners sit on `window`, in the **bubble** phase, so they are the last thing to run.
 * Every working drop target in the app has, by then, already done its job — a drop target
 * only works *because* it calls `preventDefault()`, and most also `stopPropagation()`, which
 * means the event never reaches `window` at all. When one does reach here having been
 * handled, `defaultPrevented` says so and the guard leaves the drop effect alone rather than
 * overriding the target's own "copy" cursor with "none".
 *
 * ## Why it is scoped to drags carrying files
 *
 * Preventing the default of every drop would also break the ordinary browser behaviours a
 * page is entitled to: dragging selected text into a `textarea`, a native reorder. Only a
 * file drag has a destructive default, so only a file drag is intercepted. That also leaves
 * the correspondence import's fileless-drop notice (`use-thread-import`) untouched — the
 * case it exists to explain, an Outlook-on-the-web message that carries no file, is exactly
 * the case this guard ignores.
 *
 * ## Why `dragover` and not just `drop`
 *
 * A `drop` event only fires at all when something accepted the drag by preventing the
 * `dragover` default. Guarding `drop` alone would therefore never run: the browser would
 * reject the drop and navigate. Preventing `dragover` is what makes the whole page a valid
 * (and deliberately inert) drop surface, and `dropEffect = 'none'` is what tells the reader
 * so mid-drag, with the "no drop" cursor.
 */
export function StrayFileDropGuard() {
  useEffect(() => {
    const carriesFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const handleDragOver = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      // Read before our own preventDefault, or every event looks handled.
      const acceptedByATarget = event.defaultPrevented;
      event.preventDefault();
      if (!acceptedByATarget && event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
    };

    const handleDrop = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, []);

  return null;
}
